import { flexRender, Header, Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

import { IndeterminateCheckbox } from './indeterminate-checkbox';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { DataTableRow, ColumnMeta } from '../types';
import { pinnedCellClasses } from '../lib/pinning';

interface TableHeaderProps<T extends DataTableRow> {
  table: Table<T>;
  selectable?: boolean;
  hasActions?: boolean;
  pinActions?: boolean;
}

export function DataTableHeader<T extends DataTableRow>({
  table,
  selectable = false,
  hasActions = false,
  pinActions = false
}: TableHeaderProps<T>) {
  const { t } = useTranslation();

  const hasPinnedLeftColumn = table
    .getVisibleLeafColumns()
    .some((column) => (column.columnDef.meta as ColumnMeta)?.pinned === 'left');

  return (
    // Sticks to the top of TableScrollArea, above pinned body cells and the sticky hierarchy rows (useStickyRows)
    <TableHeader className="sticky top-0 z-30 bg-card">
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {selectable && (
            <TableHead
              data-pinned={hasPinnedLeftColumn ? 'left' : undefined}
              className={cn(
                'w-12',
                // The checkbox column sits left of a pinned column — it must stick too or it scrolls away underneath
                hasPinnedLeftColumn && pinnedCellClasses('left', { header: true, edge: false })
              )}>
              <IndeterminateCheckbox
                checked={table.getIsAllRowsSelected()}
                indeterminate={table.getIsSomeRowsSelected()}
                onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
                aria-label={t('gen.select.all.label')}
              />
            </TableHead>
          )}
          {headerGroup.headers.map((header) => (
            <TableHeaderCell key={header.id} header={header} selectable={selectable} />
          ))}
          {hasActions && (
            <TableHead
              data-pinned={pinActions ? 'right' : undefined}
              // The fixed layout does not grow the cell to its content: room for RowActions' two buttons and the menu
              className={cn('w-[132px]', pinActions && pinnedCellClasses('right', { header: true }))}>
              {t('common.actions')}
            </TableHead>
          )}
        </TableRow>
      ))}
    </TableHeader>
  );
}

function TableHeaderCell<T>({ header, selectable = false }: { header: Header<T, unknown>; selectable?: boolean }) {
  const column = header.column;
  const canSort = column.getCanSort();
  const isSorted = column.getIsSorted();
  const { width, pinned } = (column.columnDef.meta as ColumnMeta | undefined) ?? {};

  return (
    <TableHead
      // Pinned cells narrow the band the scroll arrows page through (useHorizontalScroll)
      data-pinned={pinned}
      className={cn(
        width,
        canSort && 'cursor-pointer hover:bg-muted/50 transition-colors',
        pinnedCellClasses(pinned, { header: true, afterCheckbox: selectable })
      )}
      onClick={canSort ? column.getToggleSortingHandler() : undefined}>
      <div className="flex items-center gap-1">
        {flexRender(header.column.columnDef.header, header.getContext())}
        {isSorted &&
          (isSorted === 'asc' ? <ArrowUp className="w-3 h-3 shrink-0" /> : <ArrowDown className="w-3 h-3 shrink-0" />)}
      </div>
    </TableHead>
  );
}
